/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { err, ok, type AsyncResult } from '@karya/error-values';
import { idb, setupDatabase } from './Setup';
import { UpsertFailedError, WorkerNotFoundError, WorkerNotLoggedInError } from './Error';
import type { WorkerRecord } from '@karya/core';

setupDatabase().catch((error: unknown) => {
  console.error('Failed to set up database:', error);
});

/** Fetches all workers from the database. */
export async function getWorkers() {
  return await idb.getAll('worker');
}

/** Result type for {@link getWorkerById}. */
type GetWorkerByIdResult = AsyncResult<WorkerRecord, WorkerNotFoundError>;

/**
 * Fetches a worker by their ID.
 * @param id - The ID of the worker to fetch.
 * @returns An AsyncResult containing the WorkerRecord if found, or a
 * {@link WorkerNotFoundError} if not found.
 */
export async function getWorkerById(id: string): GetWorkerByIdResult {
  const worker = await idb.get('worker', id).catch((err) => {
    console.error('Error fetching worker by ID:', err);
  });
  if (!worker) {
    return err(new WorkerNotFoundError(id));
  }
  return ok(worker);
}

/** Result type for {@link getActiveWorker}. */
export type GetActiveWorkerResult = AsyncResult<WorkerRecord, WorkerNotLoggedInError | WorkerNotFoundError>;

/** Fetches the currently active (logged-in) worker.
 * @returns An AsyncResult containing the {@link WorkerRecord} of the active worker,
 * or a {@link WorkerNotLoggedInError} if no worker is logged in,
 * or a {@link WorkerNotFoundError} if the logged-in worker ID does not exist in the database.
 */
export async function getActiveWorker(): GetActiveWorkerResult {
  const loggedInWorkerId = localStorage.getItem('worker_id');
  if (!loggedInWorkerId) {
    return err(new WorkerNotLoggedInError());
  }

  return await getWorkerById(loggedInWorkerId);
}

/** Result type for {@link upsertWorker}. */
export type UpsertWorkerResult = AsyncResult<WorkerRecord, Error>;

/**
 * Inserts or updates a worker in the database.
 * @param worker - The WorkerRecord to upsert.
 *
 */
export async function upsertWorker(worker: WorkerRecord): UpsertWorkerResult {
  return await idb
    .put('worker', worker)
    .intoResult((error) => new UpsertFailedError('worker', error))
    .map(() => worker);
}

/** Result type for {@link upsertWorkers}. */
export type UpsertWorkersResult = AsyncResult<WorkerRecord[], UpsertFailedError>;

/**
 * Inserts or updates multiple workers in the database.
 * Uses the keyPath defined in the object store to determine uniqueness.
 * Conflicts are resolved by overwriting existing records with the new data.
 *
 * @param workers - An array of WorkerRecord to upsert.
 */
export async function upsertWorkers(workers: WorkerRecord[]): UpsertWorkersResult {
  const tx = idb.transaction('worker', 'readwrite');
  const store = tx.objectStore('worker');
  try {
    for (const worker of workers) {
      await store.put(worker);
    }
    await tx.done;
    return ok(workers);
  } catch (error) {
    return err(new UpsertFailedError('worker', error));
  }
}
